import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, ExternalLink, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { billingApi } from '../lib/billingApi';
import { useT } from '../lib/i18n';
import { isStoreApp, isCapacitorIOS, isCapacitorNative } from '../lib/platform';

const STATUS_LABEL = {
  fr: {
    active:           { label: 'Actif',                  color: '#16A34A' },
    trialing:         { label: 'Essai gratuit',          color: '#C9A961' },
    past_due:         { label: 'Paiement en retard',     color: '#DC2626' },
    unpaid:           { label: 'Impayé',                 color: '#DC2626' },
    canceled:         { label: 'Annulé',                 color: '#6B7280' },
    incomplete:       { label: 'Incomplet',              color: '#DC2626' },
    pending_checkout: { label: 'Paiement en attente',    color: '#6B7280' },
    none:             { label: 'Aucun abonnement',       color: '#6B7280' },
  },
  en: {
    active:           { label: 'Active',                 color: '#16A34A' },
    trialing:         { label: 'Free trial',             color: '#C9A961' },
    past_due:         { label: 'Payment past due',       color: '#DC2626' },
    unpaid:           { label: 'Unpaid',                 color: '#DC2626' },
    canceled:         { label: 'Canceled',               color: '#6B7280' },
    incomplete:       { label: 'Incomplete',             color: '#DC2626' },
    pending_checkout: { label: 'Payment pending',        color: '#6B7280' },
    none:             { label: 'No subscription',        color: '#6B7280' },
  },
};

const PLAN_LABEL = {
  fr: {
    weekly:           'Hebdomadaire — 10 $/semaine',
    weekly_solo:      'Hebdomadaire Solo — 10 $/semaine',
    monthly:          'Mensuel — 29,99 $/mois',
    monthly_solo:     'Solo mensuel',
    monthly_team:     'Équipe mensuel',
    monthly_pro:      'Pro Illimité mensuel',
    annual:           'Annuel — 265 $/an',
    annual_solo:      'Solo annuel',
    annual_team:      'Équipe annuel',
    annual_pro:       'Pro Illimité annuel',
    owner_free:       'Membre fondateur — Accès illimité',
  },
  en: {
    weekly:           'Weekly — $10/week',
    weekly_solo:      'Solo Weekly — $10/week',
    monthly:          'Monthly — $29.99/month',
    monthly_solo:     'Solo Monthly',
    monthly_team:     'Team Monthly',
    monthly_pro:      'Unlimited Pro Monthly',
    annual:           'Annual — $265/year',
    annual_solo:      'Solo Annual',
    annual_team:      'Team Annual',
    annual_pro:       'Unlimited Pro Annual',
    owner_free:       'Founding member — Unlimited access',
  },
};

/** Format an ISO date as a French long date. */
function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('fr-CA', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return null; }
}

/** Number of full days between now and an ISO date. Negative if past. */
function daysUntil(iso) {
  if (!iso) return null;
  try {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

export function SubscriptionCard() {
  const { t, lang } = useT();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    billingApi
      .status()
      .then(setStatus)
      .catch(() => setStatus({ status: 'none' }))
      .finally(() => setLoading(false));
  }, []);

  const openPortal = async () => {
    if (!navigator.onLine) {
      toast.error(lang === 'fr'
        ? 'Hors ligne — la gestion d\'abonnement nécessite une connexion internet.'
        : 'Offline — managing your subscription requires an internet connection.');
      return;
    }
    setOpeningPortal(true);
    try {
      // 🍎 Apple Guideline 3.1.3 — abonnements Apple IAP doivent être gérés
      // depuis les Réglages iOS, PAS depuis un portail web Stripe (qui
      // n'existerait même pas pour un user IAP). On détecte l'abonnement IAP
      // par: a) la source du backend, OU b) le flag local sp.iapPurchased.v1
      // (posé après un achat IAP réussi), OU c) on est sur Capacitor iOS et
      // l'abonnement est actif (le natif gère TOUJOURS via Apple).
      let isAppleIap = false;
      try {
        isAppleIap =
          (status?.source && String(status.source).includes('apple')) ||
          window.localStorage.getItem('sp.iapPurchased.v1') !== null ||
          (isCapacitorIOS() && ['active', 'trialing'].includes(status?.status));
      } catch { /* localStorage disabled */ }

      if (isAppleIap) {
        // Ouvre la page de gestion d'abonnement Apple. Sur iOS natif, le
        // scheme itms-apps:// pointe vers Réglages → Apple ID → Abonnements.
        const appleUrl = 'itms-apps://apps.apple.com/account/subscriptions';
        const webFallback = 'https://apps.apple.com/account/subscriptions';
        try {
          if (isCapacitorNative()) {
            const { Browser } = await import('@capacitor/browser');
            // Browser.open ne supporte pas itms-apps://, on utilise window.open
            // qui est intercepté par iOS et redirige vers Réglages.
            window.location.href = appleUrl;
          } else {
            window.location.href = webFallback;
          }
        } catch {
          window.location.href = webFallback;
        }
        // On ne reset pas openingPortal car la page va se déconnecter de l'app.
        return;
      }

      const { url } = await billingApi.openPortal();
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || (lang === 'fr'
        ? 'Impossible d\'ouvrir le portail'
        : 'Unable to open the portal'));
      setOpeningPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-4 flex items-center gap-3" data-testid="subscription-card-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">{lang === 'fr' ? 'Chargement de l\'abonnement…' : 'Loading subscription…'}</span>
      </div>
    );
  }

  const s = status?.status || 'none';
  const langKey = lang === 'en' ? 'en' : 'fr';
  const labels = STATUS_LABEL[langKey];
  const info = labels[s] || labels.none;
  const plan = status?.plan;
  const planLabels = PLAN_LABEL[langKey];
  const planLabel = plan ? (planLabels[plan] || plan) : null;
  const isOwnerFree = plan === 'owner_free';
  const isAutoTrial = status?.auto_trial === true;
  // hasSub = real Stripe subscription (Stripe portal available)
  const hasSub = !['none', 'pending_checkout'].includes(s) && !isAutoTrial && !isOwnerFree;

  const periodEnd = status?.current_period_end;
  const trialEnd  = status?.trial_end;
  const periodEndFmt = fmtDate(periodEnd);
  const trialEndFmt  = fmtDate(trialEnd);
  // Compute days remaining (priority: period > trial)
  const daysRemaining = daysUntil(periodEnd || trialEnd);

  return (
    <div className="card p-4" data-testid="subscription-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isOwnerFree ? (
            <ShieldCheck className="w-5 h-5" style={{ color: '#16A34A' }} />
          ) : (
            <Crown className="w-5 h-5" style={{ color: '#C9A961' }} />
          )}
          <h3 className="font-semibold text-base">{t('dash.sub.subscription')}</h3>
        </div>
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{
            backgroundColor: '#FAF7F2',
            color: isOwnerFree ? '#16A34A' : info.color,
            border: `1px solid ${(isOwnerFree ? '#16A34A' : info.color)}33`,
          }}
          data-testid="subscription-status-badge"
        >
          {isOwnerFree ? (lang === 'fr' ? 'Membre fondateur' : 'Founding member') : (s === 'trialing' ? t('dash.sub.trial') : info.label)}
        </span>
      </div>

      {/* Body */}
      <div className="text-sm space-y-1 mb-4" style={{ color: '#374151' }}>
        {plan && (
          <div data-testid="subscription-plan-label">
            {lang === 'fr' ? 'Plan :' : 'Plan:'} <strong>{planLabel}</strong>
          </div>
        )}

        {isAutoTrial && (
          <div>{t('dash.sub.trial_auto')}</div>
        )}

        {/* Days remaining banner — VERY visible */}
        {!isAutoTrial && daysRemaining !== null && daysRemaining > 0 && (
          <div
            className="mt-3 p-3 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#15803D' }}
            data-testid="subscription-days-remaining"
          >
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <div className="text-sm">
              <strong>
                {daysRemaining}{' '}
                {lang === 'fr'
                  ? (daysRemaining === 1 ? 'jour restant' : 'jours restants')
                  : (daysRemaining === 1 ? 'day left' : 'days left')}
              </strong>
              {periodEndFmt && (
                <span style={{ color: '#16A34A' }}> · {lang === 'fr' ? 'jusqu\'au' : 'until'} {periodEndFmt}</span>
              )}
              {!periodEndFmt && trialEndFmt && (
                <span style={{ color: '#16A34A' }}> · {lang === 'fr' ? 'jusqu\'au' : 'until'} {trialEndFmt}</span>
              )}
            </div>
          </div>
        )}

        {/* Stripe-specific renewal info (paid plans only) */}
        {s === 'trialing' && trialEndFmt && !isOwnerFree && (
          <div className="mt-1 text-xs" style={{ color: '#6B7280' }}>
            {t('dash.sub.trial_end')} <strong>{trialEndFmt}</strong>
          </div>
        )}
        {s === 'active' && periodEndFmt && !isOwnerFree && (
          <div className="mt-1 text-xs" style={{ color: '#6B7280' }}>
            {lang === 'fr' ? 'Prochain renouvellement :' : 'Next renewal:'} <strong>{periodEndFmt}</strong>
          </div>
        )}
        {status?.cancel_at_period_end && (
          <div className="mt-2 text-sm" style={{ color: '#DC2626' }}>
            {lang === 'fr'
              ? 'L\'abonnement sera annulé à la fin de la période en cours.'
              : 'Subscription will be canceled at the end of the current period.'}
          </div>
        )}
      </div>

      {/* Actions */}
      {isOwnerFree ? (
        <div
          className="rounded-lg px-4 py-3 text-sm text-center"
          style={{ backgroundColor: '#FAF7F2', color: '#5A1620', border: '1px solid #C9A961' }}
          data-testid="subscription-owner-free-message"
        >
          {lang === 'fr'
            ? '✨ Tu as un accès illimité gratuit. Aucun paiement requis.'
            : '✨ You have unlimited free access. No payment required.'}
        </div>
      ) : hasSub ? (
        <button
          onClick={openPortal}
          disabled={openingPortal}
          className="btn-outline w-full gap-2 !text-sm"
          data-testid="subscription-manage-btn"
        >
          {openingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          {lang === 'fr' ? 'Gérer mon abonnement' : 'Manage subscription'}
        </button>
      ) : isStoreApp() ? (
        // Apple Guideline 3.1.1 / Google Play Billing — pas de CTA d'achat
        // dans les apps de magasin. Message neutre et passif.
        <div
          className="text-center text-xs px-3 py-3 rounded-lg"
          style={{ backgroundColor: '#FAF7F2', color: '#6B7280', border: '1px solid #E5DCC9' }}
          data-testid="subscription-store-only-notice"
        >
          {lang === 'fr'
            ? "Les abonnements ne sont pas disponibles depuis cette app."
            : 'Subscriptions are not available from this app.'}
        </div>
      ) : (
        <Link
          to="/pricing"
          className="w-full inline-flex items-center justify-center py-2.5 rounded-lg font-semibold text-white text-sm gap-2"
          style={{ backgroundColor: '#7A1F2B' }}
          data-testid="subscription-upgrade-btn"
        >
          {isAutoTrial
            ? t('dash.sub.choose_plan')
            : (() => {
                // 🍎 Apple Guideline 3.1.2: ne plus dire "Essai 3 jours gratuit"
                // si l'utilisateur a déjà consommé son essai (auto_trial_expired
                // ou trial_end existant). Sinon = fausse promesse = rejet.
                const trialConsumed = status?.auto_trial_expired === true || !!status?.trial_end;
                if (trialConsumed) {
                  return lang === 'fr' ? 'Voir les plans' : 'View plans';
                }
                return lang === 'fr' ? 'Voir les plans — Essai 3 jours gratuit' : 'View plans — 3-day free trial';
              })()}
        </Link>
      )}
    </div>
  );
}
